import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Button,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import RoleForm from '../../../components/Forms/RoleForm';
import { roleService } from '../../../services/apiService';
import { showToast } from '../../../utils/toast';

const RolesTab = forwardRef(({ onAddRef }, ref) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await roleService.getAll();
      if (response.success) {
        setRoles(response.data || []);
      } else {
        setError(response.error || 'Failed to fetch roles');
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError(err.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleAdd = useCallback(() => {
    setEditingRole(null);
    setModalOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({
    triggerAdd: handleAdd,
  }));

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);

  const handleEdit = (role) => {
    setEditingRole(role);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    if (modalLoading) return;
    setModalOpen(false);
    setEditingRole(null);
  };

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      setError(null);
      let response;
      if (editingRole) {
        response = await roleService.update(editingRole.id, formData);
      } else {
        response = await roleService.create(formData);
      }
      if (response && !response.success) {
        const errorMsg = response.error || 'Failed to save role';
        showToast.error(errorMsg);
        return;
      }
      showToast.success(`Role "${formData.role_name}" ${editingRole ? 'updated' : 'created'} successfully`);
      await fetchRoles();
      handleModalClose();
    } catch (err) {
      console.error('Error saving role:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save role';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setModalLoading(false);
    }
  }, [editingRole]);

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : roles.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No roles found
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {roles.map((role) => (
            <Paper
              key={role.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 2,
                borderColor: 'divider',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Chip
                    label={role.role_name}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.95rem', py: 2.5, px: 1 }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(role)}
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  Edit
                </Button>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <RoleForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editingRole}
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        loading={modalLoading}
      />
    </Box>
  );
});

RolesTab.displayName = 'RolesTab';

export default RolesTab;
